export interface AvailableSlotOption {
  id: string;
  day_part: "AM" | "PM";
  display_time: string;
  condition_sort_order: number;
}

export interface AvailableSlotReference {
  id: string;
  label: string;
  active: boolean;
}

export interface AvailableTimeCell {
  weekday: number;
  time_slot_id: string;
}

export interface AvailableTimeCellReference {
  weekday: number;
  time_slot: AvailableSlotReference;
}

export type TimePreference = "ANY" | "AM" | "PM";
type AvailableTimeMode = TimePreference | "CUSTOM";

export function normalizeAvailableTimeSelection(
  timePreference: TimePreference,
  references: AvailableTimeCellReference[] | null,
  slots: AvailableSlotOption[]
): { timePreference: TimePreference; availableTimeCells: AvailableTimeCell[] | null } {
  if (references === null) {
    return { timePreference, availableTimeCells: null };
  }
  const activeById = new Map(slots.map((slot) => [slot.id, slot]));
  return {
    timePreference: "ANY",
    availableTimeCells: references
      .filter((reference) => {
        const slot = activeById.get(reference.time_slot.id);
        return !slot || timePreference === "ANY" || slot.day_part === timePreference;
      })
      .map((reference) => ({
        weekday: reference.weekday,
        time_slot_id: reference.time_slot.id
      }))
  };
}

export function availableTimeSummary(
  timePreference: TimePreference,
  references: AvailableSlotReference[] | null,
  _slots: AvailableSlotOption[],
  cellReferences?: AvailableTimeCellReference[] | null
): string {
  if (cellReferences) {
    const byWeekday = new Map<number, string[]>();
    for (const cell of cellReferences) {
      const label = cell.time_slot.active
        ? cell.time_slot.label
        : `${cell.time_slot.label}（已删除）`;
      byWeekday.set(cell.weekday, [...(byWeekday.get(cell.weekday) ?? []), label]);
    }
    return [...byWeekday.entries()]
      .sort(([left], [right]) => left - right)
      .map(([weekday, labels]) => `${weekdayLabel(weekday)}：${labels.join("、")}`)
      .join("；");
  }
  if (references === null) {
    if (timePreference === "AM") return "仅上午";
    if (timePreference === "PM") return "仅下午";
    return "不限";
  }
  const labels = new Map(references.map((reference) => [reference.id, reference]));
  return references
    .map((reference) => reference.id)
    .map((id) => {
      const reference = labels.get(id);
      if (!reference) return id;
      return reference.active ? reference.label : `${reference.label}（已删除）`;
    })
    .join("、");
}

export function isAvailableTimeSelectionValid(
  timePreference: TimePreference,
  value: AvailableTimeCell[] | null,
  slots: AvailableSlotOption[]
): boolean {
  if (value !== null) return value.length > 0;
  if (timePreference === "ANY") return slots.length > 0;
  return slots.some((slot) => slot.day_part === timePreference);
}

export function AvailableTimeSlotsField({
  slots,
  timePreference,
  value,
  currentReferences = [],
  disabled = false,
  onChange
}: {
  slots: AvailableSlotOption[];
  timePreference: TimePreference;
  value: AvailableTimeCell[] | null;
  currentReferences?: AvailableTimeCellReference[];
  disabled?: boolean;
  onChange: (
    timePreference: TimePreference,
    value: AvailableTimeCell[] | null
  ) => void;
}): React.JSX.Element {
  const selected = value ?? [];
  const active = [...slots].sort(
    (left, right) =>
      left.condition_sort_order - right.condition_sort_order ||
      left.id.localeCompare(right.id)
  );
  const activeIds = new Set(active.map((slot) => slot.id));
  const deleted = currentReferences.filter(
    (reference) =>
      !reference.time_slot.active && !activeIds.has(reference.time_slot.id)
  );
  const partIndex = new Map<string, number>();
  for (const part of ["AM", "PM"] as const) {
    active
      .filter((slot) => slot.day_part === part)
      .forEach((slot, index) => partIndex.set(slot.id, index + 1));
  }
  const mode: AvailableTimeMode = value === null ? timePreference : "CUSTOM";
  const presetSlots = active.filter(
    (slot) => mode === "ANY" || slot.day_part === mode
  );

  function changeMode(nextMode: AvailableTimeMode): void {
    if (nextMode === "CUSTOM") {
      const initial =
        value ?? WEEKDAYS.flatMap((weekday) =>
          active
            .filter(
              (slot) => timePreference === "ANY" || slot.day_part === timePreference
            )
            .map((slot) => ({ weekday: weekday.value, time_slot_id: slot.id }))
        );
      onChange("ANY", initial);
      return;
    }
    onChange(nextMode, null);
  }

  function isSelected(weekday: number, slotId: string): boolean {
    return selected.some(
      (cell) => cell.weekday === weekday && cell.time_slot_id === slotId
    );
  }

  function setCells(cells: AvailableTimeCell[], checked: boolean): void {
    const keys = new Set(cells.map(cellKey));
    const retained = selected.filter((cell) => !keys.has(cellKey(cell)));
    onChange("ANY", checked ? [...retained, ...cells] : retained);
  }

  function toggleCell(weekday: number, slotId: string): void {
    const cell = { weekday, time_slot_id: slotId };
    setCells([cell], !isSelected(weekday, slotId));
  }

  function toggleDay(weekday: number): void {
    const cells = active.map((slot) => ({ weekday, time_slot_id: slot.id }));
    setCells(cells, !cells.every((cell) => isSelected(cell.weekday, cell.time_slot_id)));
  }

  function toggleSlot(slotId: string): void {
    const cells = WEEKDAYS.map((weekday) => ({
      weekday: weekday.value,
      time_slot_id: slotId
    }));
    setCells(cells, !cells.every((cell) => isSelected(cell.weekday, cell.time_slot_id)));
  }

  return (
    <fieldset className="available-slots-field" disabled={disabled}>
      <legend>可用节次</legend>
      <div className="available-mode-row">
        <label className="field available-mode-select">
          <span>可排方式</span>
          <select
            aria-label="可排方式"
            value={mode}
            onChange={(event) => changeMode(event.target.value as AvailableTimeMode)}
          >
            <option value="ANY">不限</option>
            <option value="AM">仅上午</option>
            <option value="PM">仅下午</option>
            <option value="CUSTOM">指定节次</option>
          </select>
        </label>
        {mode !== "CUSTOM" ? (
          <div className="available-preset-summary" aria-label="当前可排节次">
            <span>对应节次</span>
            <strong>
              {presetSlots.length > 0
                ? presetSlots.map((slot) => slotLabel(slot, partIndex)).join("、")
                : `当前没有${mode === "AM" ? "上午" : mode === "PM" ? "下午" : ""}节次`}
            </strong>
          </div>
        ) : null}
      </div>
      {mode === "CUSTOM" ? (
        <div className="available-matrix-scroll">
          <table className="available-time-matrix">
            <thead>
              <tr>
                <th scope="col">节次 / 星期</th>
                {WEEKDAYS.map((weekday) => {
                  const allSelected = active.every((slot) =>
                    isSelected(weekday.value, slot.id)
                  );
                  return (
                    <th scope="col" key={weekday.value}>
                      <button
                        type="button"
                        aria-pressed={allSelected}
                        title={`批量${allSelected ? "取消" : "选择"}${weekday.label}`}
                        onClick={() => toggleDay(weekday.value)}
                      >
                        {weekday.label}
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {active.map((slot) => {
                const allSelected = WEEKDAYS.every((weekday) =>
                  isSelected(weekday.value, slot.id)
                );
                return (
                  <tr key={slot.id}>
                    <th scope="row">
                      <button
                        className="slot-heading"
                        type="button"
                        aria-pressed={allSelected}
                        title={`批量${allSelected ? "取消" : "选择"}${slotLabel(slot, partIndex)}`}
                        onClick={() => toggleSlot(slot.id)}
                      >
                        <strong>{slotLabel(slot, partIndex)}</strong>
                        <small>{slot.display_time}</small>
                      </button>
                    </th>
                    {WEEKDAYS.map((weekday) => {
                      const checked = isSelected(weekday.value, slot.id);
                      return (
                        <td key={weekday.value}>
                          <button
                            type="button"
                            aria-pressed={checked}
                            aria-label={`${weekday.label}${slotLabel(slot, partIndex)}${checked ? "，已选择" : "，未选择"}`}
                            onClick={() => toggleCell(weekday.value, slot.id)}
                          >
                            {checked ? "可排" : "—"}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {deleted.length > 0 ? (
            <div className="available-slot-group deleted-slot-group">
              <strong>已删除</strong>
              <div className="available-slot-options">
                {deleted.map((cell) => (
                  <label key={`${cell.weekday}:${cell.time_slot.id}`}>
                    <input
                      type="checkbox"
                      checked={isSelected(cell.weekday, cell.time_slot.id)}
                      onChange={(event) =>
                        setCells(
                          [{ weekday: cell.weekday, time_slot_id: cell.time_slot.id }],
                          event.target.checked
                        )
                      }
                    />
                    {weekdayLabel(cell.weekday)} · {cell.time_slot.label}（已删除）
                  </label>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      {mode === "CUSTOM" && value?.length === 0 ? (
        <small className="field-error" role="alert">至少选择一个节次</small>
      ) : null}
      {mode !== "CUSTOM" && presetSlots.length === 0 ? (
        <small className="field-error" role="alert">请先在基础资料中添加对应节次</small>
      ) : null}
    </fieldset>
  );
}

const WEEKDAYS = [
  { value: 1, label: "周一" },
  { value: 2, label: "周二" },
  { value: 3, label: "周三" },
  { value: 4, label: "周四" },
  { value: 5, label: "周五" }
] as const;

function weekdayLabel(weekday: number): string {
  return WEEKDAYS.find((item) => item.value === weekday)?.label ?? `星期${weekday}`;
}

function cellKey(cell: AvailableTimeCell): string {
  return `${cell.weekday}:${cell.time_slot_id}`;
}

function slotLabel(
  slot: AvailableSlotOption,
  partIndex: Map<string, number>
): string {
  return `${slot.day_part === "AM" ? "上午" : "下午"}第 ${partIndex.get(slot.id)} 节`;
}
